export type PatientInfo = {
	id: number
	email?: string
	phone?: string
	firstName?: string
	lastName?: string
	status: string
	isOnboarded: boolean
	signupSettings: Record<string, number>
}

export type PatientBillingStatus = 'ACTIVE' | 'INACTIVE'
export type PatientBillingChargeType = 'ONE_TIME' | 'SUBSCRIPTION' | 'UNKNOWN'
export type PatientBillingChargeStatus =
	| 'SUCCEEDED'
	| 'PENDING'
	| 'FAILED'
	| 'REFUNDED'
	| 'DISPUTED'
	| 'PARTIALLY_REFUNDED'
	| 'EXPIRED'
	| 'CANCELED'

export type PatientBillingPrimaryCard = {
	id: string
	cardName: string
	lastFourDigits: string
	expirationMonth: number
	expirationYear: number
	isDefault: boolean
}

/** The receipt endpoint's refusal for a charge that never took money: FAILED, PENDING, EXPIRED, CANCELED. */
export const CHARGE_NOT_PAID = 'CHARGE_NOT_PAID'

export type PatientBillingCharge = {
	id: string
	amountInCents: number
	status: PatientBillingChargeStatus
	description: string
	date: string
	chargeType: PatientBillingChargeType
	/** Set once any of the charge has been refunded; absent on servers from before the field was added. */
	refundedInCents?: number
	paymentId: number
}

export type PatientBillingSubscriptionStatus =
	| 'ACTIVE'
	| 'PAUSED'
	| 'SUSPENDED'
	| 'CANCELED'
	| 'PENDING'
	| 'UNKNOWN'

export type PatientBillingSubscription = {
	planName: string
	costInCents: number
	/** The billing cadence as the server names it, e.g. MONTHLY. */
	planType: string
	status: PatientBillingSubscriptionStatus
	renewalDate: string
}

export type PatientBillingRecord = {
	status: PatientBillingStatus
	primaryCard: PatientBillingPrimaryCard | null
	charges: PatientBillingCharge[]
	/** Absent on servers from before the field was added; null when the patient has no plan. */
	subscription?: PatientBillingSubscription | null
}

export type PaymentPlanStatus =
	| 'PENDING'
	| 'PAID_OFF'
	| 'DELINQUENT'
	| 'REFUNDED'
	| string

export type PaymentPlanInstalment = {
	name: string
	amountInCents: number
	/** yyyy-MM-dd */
	dueOn: string
	/** PENDING until the instalment is invoiced; kept nullable so an older shape cannot crash the tiles. */
	paymentStatus: PatientBillingChargeStatus | null
}

export type PaymentPlan = {
	id: number
	name: string
	status: PaymentPlanStatus
	instalments: PaymentPlanInstalment[]
}

export type PatientMembershipStatus = 'ACTIVE' | 'INACTIVE'

export type PatientMembershipCheckResponse = {
	status: PatientMembershipStatus
}

/** A SetupIntent to confirm, and for a mobile SDK an ephemeral key to read the customer's cards with. */
export type CardSetup = {
	setupIntentId: string
	clientSecret: string
	customerId: string
	ephemeralKeySecret: string | null
}

export type WhiteningOrderKind = 'KIT' | 'GEL'

export type WhiteningOrderStatus =
	| 'REQUESTED'
	| 'BEING_MADE'
	| 'SHIPPED'
	| 'DELIVERED'
	| 'CANCELLED'

/**
 * Where the order has got to, as the member would say it. `status` is the fulfilment team's
 * word and `stage` is the patient's: a REQUESTED kit is AWAITING_VISIT while we still need the
 * scan, and BEING_MADE once we have it, which is the distinction the screen is built on.
 */
export type WhiteningStage =
	| 'AWAITING_VISIT'
	| 'BEING_MADE'
	| 'SHIPPED'
	| 'DELIVERED'
	| 'CANCELLED'

export type WhiteningShipTo = {
	name: string
	street1: string
	street2?: string
	city: string
	state: string
	postalCode: string
}

export type WhiteningOrder = {
	id: number
	kind: WhiteningOrderKind
	status: WhiteningOrderStatus
	stage: WhiteningStage
	requestedAt: string
	carrier?: string
	trackingNumber?: string
	/**
	 * The carrier's own page for this parcel, built by the server.
	 *
	 * Not derived here from the carrier and the number, which is where it used to live: a carrier
	 * the app had never heard of would then need an App Store release before anyone could track a
	 * parcel. Absent when we do not know where that carrier's page is, and an absent link means
	 * the row does not open anything — a wrong tracking link is worse than none.
	 */
	trackingUrl?: string
	shippedAt?: string
	deliveredAt?: string
	/** Where this parcel is going: the address as it stood when the order was placed. */
	shipTo: WhiteningShipTo
}

/**
 * The whitening kit in one read: the live order if there is one, and what the member may order
 * next.
 *
 * Every absent value is omitted by the server rather than sent as null, which is why these are
 * optional rather than nullable. `scanOnFile` says whether we already hold the 3D scan taken at
 * a first visit — the one fact the pre-claim copy turns on, because a kit can only be made from
 * a scan and a claim without one waits for the visit.
 */
export type WhiteningSummary = {
	/**
	 * The most recent order of each kind, independently, and absent where that kind has never
	 * been ordered. `stage` is derived per kind.
	 *
	 * Two fields rather than one order, because one could not describe the page: a member with a
	 * refill in flight still has a delivered kit, and that kit's tracking number is the receipt
	 * for a parcel which arrived weeks ago. A single `order` made the newer of the two hide the
	 * other.
	 */
	kit?: WhiteningOrder
	gel?: WhiteningOrder
	canOrderKit: boolean
	canOrderGel: boolean
	/** ISO date the next refill opens, while the member is inside the four-week wait. */
	gelAvailableAt?: string
	scanOnFile: boolean
	/**
	 * Where the member's things ship today, whether or not anything is on its way, and absent if
	 * they have never given us one.
	 *
	 * Deliberately not the same field as an order's own `shipTo`: that is the snapshot taken when
	 * that order was placed and is where that parcel went, while this is the address a new order
	 * would use. They can legitimately differ, which is why both are here — and why the sheet
	 * that places an order reads this one and never the snapshot.
	 */
	shippingAddress?: WhiteningShipTo
}

/** The refusals `POST /whitening/orders` answers with, carried on the error body's `code`. */
export type WhiteningOrderErrorCode =
	| 'KIT_ALREADY_ORDERED'
	| 'GEL_BEFORE_KIT'
	| 'GEL_TOO_SOON'
	| 'SHIPPING_ADDRESS_MISSING'
